import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";

import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useSearchParams } from "next/navigation";

import useUser from "@/users/useUser";

dayjs.extend(utc);
dayjs.extend(timezone);

export const SALESFORCE_CHAT_START = 8; // 8 am EST
export const SALESFORCE_CHAT_END = 20; // 8 pm EST

// Salesforce event names
const SALESFORCE_EVENTS = {
  WITHIN_BUSINESS_HOURS: "onEmbeddedMessagingWithinBusinessHours",
  BUSINESS_HOURS_STARTED: "onEmbeddedMessagingBusinessHoursStarted",
  BUSINESS_HOURS_ENDED: "onEmbeddedMessagingBusinessHoursEnded",
  MESSAGING_READY: "onEmbeddedMessagingReady",
  CONVERSATION_STARTED: "onEmbeddedMessagingConversationStarted",
} as const;

export interface SalesforceConfig {
  /** Salesforce chat key/ID */
  key: string;
  /** Salesforce site endpoint URL */
  siteEndpoint: string;
  /** Salesforce SCRT2 URL */
  scrt2Url: string;
  /** Label for the messaging session (e.g., "Pre_Authentication_Messaging_Session") */
  sessionLabel: string;
  /** Website identifier for Salesforce pre-chat fields */
  websiteLabel: string;
  /** Session storage key for tracking active chat state */
  chatActiveKey: string;
}

export interface EmbeddedChatService {
  init(
    id: string,
    label: string,
    messagingUrl: string,
    options: { scrt2URL: string },
  ): void;
  prechatAPI: {
    setHiddenPrechatFields(options: { Website: string }): void;
  };
  settings: {
    hideChatButtonOnLoad: boolean;
    language: string;
  };
  utilAPI: {
    launchChat: () => Promise<void>;
  };
}

declare global {
  interface Window {
    embeddedservice_bootstrap?: EmbeddedChatService;
  }
}

export interface SalesforceChatSupportContextValue {
  isChatReady: boolean;
  launchChat: () => void;
}

export const UnifiedSalesforceChatSupportContext = createContext<
  SalesforceChatSupportContextValue | undefined
>(undefined);

interface UnifiedSalesforceChatSupportProps {
  children: ReactNode;
}

/**
 * Unified Salesforce chat support provider that handles both authenticated
 * and unauthenticated states. Automatically switches between login and
 * authenticated chat configs based on user authentication status.
 */
export const UnifiedSalesforceChatSupport = ({
  children,
}: UnifiedSalesforceChatSupportProps) => {
  const { data: user, loading: userLoading } = useUser();
  const isAuthenticated = Boolean(user && !userLoading);
  const isUserDataReady = !userLoading;

  // Memoized configs to prevent recreating on every render
  const loginConfig: SalesforceConfig = useMemo(
    () => ({
      key: process.env["NEXT_PUBLIC_SALESFORCE_LOGIN_SUPPORT_CHAT_KEY"] ?? "",
      siteEndpoint:
        process.env[
          "NEXT_PUBLIC_SALESFORCE_LOGIN_SUPPORT_CHAT_SITE_ENDPOINT"
        ] ?? "",
      scrt2Url:
        process.env["NEXT_PUBLIC_SALESFORCE_LOGIN_SUPPORT_CHAT_SCRT2URL"] ?? "",
      sessionLabel: "Pre_Authentication_Messaging_Session",
      websiteLabel: "Pre-Authentication Newsela",
      chatActiveKey: "newsela_login_support_chat_active",
    }),
    [],
  );

  const authenticatedConfig: SalesforceConfig = useMemo(
    () => ({
      key:
        process.env["NEXT_PUBLIC_SALESFORCE_AUTHENTICATED_SUPPORT_CHAT_KEY"] ??
        "",
      siteEndpoint:
        process.env[
          "NEXT_PUBLIC_SALESFORCE_AUTHENTICATED_SUPPORT_CHAT_SITE_ENDPOINT"
        ] ?? "",
      scrt2Url:
        process.env[
          "NEXT_PUBLIC_SALESFORCE_AUTHENTICATED_SUPPORT_CHAT_SCRT2URL"
        ] ?? "",
      sessionLabel: "Messaging_Session",
      websiteLabel: "Authenticated Newsela",
      chatActiveKey: "newsela_authenticated_support_chat_active",
    }),
    [],
  );

  const config = isAuthenticated ? authenticatedConfig : loginConfig;

  const hasRequiredConfig = Boolean(
    config.key &&
      config.siteEndpoint &&
      config.scrt2Url &&
      config.sessionLabel &&
      config.websiteLabel,
  );

  const [isChatLoaded, setIsChatLoaded] = useState(false);
  const [isChatReady, setIsChatReady] = useState(false);
  const params = useSearchParams();
  const eventListenersRef = useRef<(() => void)[]>([]);
  const bootstrapScriptRef = useRef<HTMLScriptElement | null>(null);
  const initScriptRef = useRef<HTMLScriptElement | null>(null);

  const cleanupEventListeners = useCallback(() => {
    eventListenersRef.current.forEach((cleanup) => cleanup());
    eventListenersRef.current = [];
  }, []);

  // Check if office hours are overridden via query param
  const isOfficeHoursOverridden = params?.get("officeHours") === "false";

  // Check if current time is within support hours (M-F, 8am-8pm EST)
  const isWithinOfficeHours = useCallback(() => {
    if (isOfficeHoursOverridden) return false;

    const now = dayjs().tz("America/New_York");
    const dayOfWeek = now.day();
    const currentHour = now.hour();

    const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;
    const isDuringHours =
      currentHour >= SALESFORCE_CHAT_START &&
      currentHour <= SALESFORCE_CHAT_END;

    return isWeekday && isDuringHours;
  }, [isOfficeHoursOverridden]);

  // Load and initialize Salesforce chat script
  useEffect(() => {
    if (!isUserDataReady || !hasRequiredConfig) {
      return;
    }

    const cleanup = () => {
      // Remove scripts
      bootstrapScriptRef.current?.remove();
      bootstrapScriptRef.current = null;
      initScriptRef.current?.remove();
      initScriptRef.current = null;

      // Cleanup event listeners
      cleanupEventListeners();

      // Remove Salesforce UI elements
      document
        .querySelectorAll(
          '[class*="embeddedMessaging"], [id*="embeddedMessaging"]',
        )
        .forEach((element) => element.remove());

      // Clear session storage for BOTH configs
      sessionStorage.removeItem(loginConfig.chatActiveKey);
      sessionStorage.removeItem(authenticatedConfig.chatActiveKey);

      // Clear Salesforce's own localStorage keys
      Object.keys(localStorage).forEach((key) => {
        if (
          key.startsWith("ESW") ||
          key.startsWith("SFDC") ||
          key.includes("embeddedservice") ||
          key.includes("embedded_svc") ||
          key.includes("snapins") ||
          key.includes("scrt2")
        ) {
          localStorage.removeItem(key);
        }
      });

      // Clear window object
      delete window.embeddedservice_bootstrap;

      // Reset state
      setIsChatLoaded(false);
      setIsChatReady(false);
    };

    // Clean up any existing instance first
    cleanup();

    // Create bootstrap script
    const bootstrapScript = document.createElement("script");
    bootstrapScript.type = "text/javascript";
    bootstrapScript.src = `${config.siteEndpoint}/assets/js/bootstrap.min.js`;
    bootstrapScript.id = "salesforce-bootstrap-script";

    bootstrapScript.onload = () => {
      // Create inline initialization script
      const initScript = document.createElement("script");
      initScript.type = "text/javascript";
      initScript.id = "salesforce-init-script";
      initScript.innerHTML = `
        function initEmbeddedMessaging() {
          try {
            embeddedservice_bootstrap.settings.language = 'en_US';
            embeddedservice_bootstrap.settings.hideChatButtonOnLoad = true;

            embeddedservice_bootstrap.init(
              '${config.key}',
              '${config.sessionLabel}',
              '${config.siteEndpoint}',
              { scrt2URL: '${config.scrt2Url}' }
            );
          } catch (err) {
            console.error('Error loading Embedded Messaging: ', err);
          }
        };
        initEmbeddedMessaging();
      `;

      document.head.appendChild(initScript);
      initScriptRef.current = initScript;
      setIsChatLoaded(true);
    };

    bootstrapScript.onerror = (e) => {
      console.error("Failed to load Salesforce script:", e);
    };

    document.head.appendChild(bootstrapScript);
    bootstrapScriptRef.current = bootstrapScript;

    return cleanup;
  }, [
    config,
    loginConfig,
    authenticatedConfig,
    isUserDataReady,
    hasRequiredConfig,
    cleanupEventListeners,
  ]);

  const launchChat = useCallback(() => {
    const bootstrap = window.embeddedservice_bootstrap;

    if (!bootstrap) {
      console.error("Salesforce embeddedservice_bootstrap is not defined");
      return;
    }

    if (!bootstrap.utilAPI) {
      console.error(
        "Salesforce embeddedservice_bootstrap.utilAPI is not available",
      );
      return;
    }

    bootstrap.utilAPI.launchChat().catch((error) => {
      console.error("Failed to launch Salesforce chat client:", error);
    });
  }, []);

  // Set up event listeners for Salesforce chat events
  useEffect(() => {
    if (!isChatLoaded) {
      return;
    }

    const handleBusinessHoursActive = () => {
      setIsChatReady(!isOfficeHoursOverridden);
    };

    const handleBusinessHoursInactive = () => {
      setIsChatReady(false);
    };

    const handleMessagingReady = () => {
      // Set pre-chat fields
      window.embeddedservice_bootstrap?.prechatAPI.setHiddenPrechatFields({
        Website: config.websiteLabel,
      });

      // Check if within office hours
      if (!isWithinOfficeHours()) {
        // eslint-disable-next-line no-console
        console.info(
          "Salesforce chat is outside of office hours on initialization.",
        );
        return;
      }

      setIsChatReady(true);

      // Restore active chat session if exists
      const hasActiveSession = sessionStorage.getItem(config.chatActiveKey);
      if (hasActiveSession) {
        sessionStorage.removeItem(config.chatActiveKey);
        // Small delay to ensure Salesforce is fully ready
        setTimeout(() => {
          window.embeddedservice_bootstrap?.utilAPI
            ?.launchChat()
            .catch((err) => console.error("Failed to restore chat:", err));
        }, 100);
      }
    };

    const handleConversationStarted = () => {
      sessionStorage.setItem(config.chatActiveKey, "true");
    };

    // Register event listeners
    const eventHandlers: Array<[keyof typeof SALESFORCE_EVENTS, () => void]> = [
      ["WITHIN_BUSINESS_HOURS", handleBusinessHoursActive],
      ["BUSINESS_HOURS_STARTED", handleBusinessHoursActive],
      ["BUSINESS_HOURS_ENDED", handleBusinessHoursInactive],
      ["MESSAGING_READY", handleMessagingReady],
      ["CONVERSATION_STARTED", handleConversationStarted],
    ];

    eventHandlers.forEach(([eventKey, handler]) => {
      const eventName = SALESFORCE_EVENTS[eventKey];
      window.addEventListener(eventName, handler);
    });

    // Store cleanup functions
    eventListenersRef.current = eventHandlers.map(([eventKey, handler]) => {
      const eventName = SALESFORCE_EVENTS[eventKey];
      return () => window.removeEventListener(eventName, handler);
    });

    return cleanupEventListeners;
  }, [
    isChatLoaded,
    config.websiteLabel,
    config.chatActiveKey,
    isOfficeHoursOverridden,
    isWithinOfficeHours,
    cleanupEventListeners,
  ]);

  return (
    <UnifiedSalesforceChatSupportContext.Provider
      value={{ isChatReady, launchChat }}
    >
      {children}
    </UnifiedSalesforceChatSupportContext.Provider>
  );
};

UnifiedSalesforceChatSupport.displayName = "UnifiedSalesforceChatSupport";

/**
 * Hook to access the unified Salesforce chat support context.
 * Must be used within a UnifiedSalesforceChatSupport provider.
 * Works for both authenticated and unauthenticated states.
 */
export const useSalesforceChatSupport =
  (): SalesforceChatSupportContextValue => {
    const context = useContext(UnifiedSalesforceChatSupportContext);

    if (context === undefined) {
      throw new Error(
        "useSalesforceChatSupport must be used within a UnifiedSalesforceChatSupport provider",
      );
    }

    return context;
  };
